import React from 'react';
import ReactTooltip from 'react-tooltip';
import './myeventstoggle.scss';

type MyEventsToggleProps = {
    isOn: boolean;
    onChange: (isOn: boolean) => void;
    label: string;
    allLabel?: string;
    infoText?: string;
    tooltipId?: string;
    tooltipExpanded?: boolean;
    onTooltipShow?: () => void;
    onTooltipHide?: () => void;
    switchId?: string;
    ariaControls?: string;
    wrapperClassName?: string;
    onShowingText?: string;
    offShowingText?: string;
    rightContent?: React.ReactNode;
};

export default function MyEventsToggle({
    isOn,
    onChange,
    label,
    allLabel,
    infoText,
    tooltipId = 'my-events-info',
    tooltipExpanded,
    onTooltipShow,
    onTooltipHide,
    switchId = 'my-events-switch',
    ariaControls,
    wrapperClassName = '',
    onShowingText,
    offShowingText,
    rightContent,
}: MyEventsToggleProps) {
    const handleToggle = () => {
        onChange(!isOn);
    };

    const switchButton = (
        <button
            type="button"
            id={switchId}
            className={`my-events-toggle-switch${isOn ? ' is-on' : ''}`}
            role="switch"
            aria-checked={isOn}
            aria-label={
                allLabel
                    ? isOn
                        ? `${label} active. Switch to ${allLabel}`
                        : `${allLabel} active. Switch to ${label}`
                    : label
            }
            aria-controls={ariaControls}
            onClick={handleToggle}
        >
            <span className="my-events-toggle-track" aria-hidden="true">
                <span className="my-events-toggle-thumb" />
            </span>
        </button>
    );

    const infoButton = infoText ? (
        <>
            <button
                data-for={tooltipId}
                data-tip={infoText}
                data-event="focus mouseenter"
                data-event-off="blur mouseleave"
                aria-label={infoText}
                aria-expanded={tooltipExpanded}
                aria-controls={tooltipId}
                aria-describedby={tooltipId}
                className="my-events-info-button"
                type="button"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.stopPropagation();
                        ReactTooltip.show(e.currentTarget as HTMLElement);
                    }
                }}
            >
                <span className="icon my-events-icon" aria-hidden="true" />
            </button>
            <ReactTooltip
                id={tooltipId}
                place="right"
                type="dark"
                effect="solid"
                multiline={true}
                clickable={true}
                delayHide={200}
                afterShow={onTooltipShow}
                afterHide={onTooltipHide}
            />
        </>
    ) : null;

    return (
        <div id={`${switchId}-container`} className={`my-events-toggle-container ${wrapperClassName}`.trim()}>
            <div className="my-events-toggle-row">
                {allLabel ? (
                    <>
                        <span
                            id={`${switchId}-all-label`}
                            className={`my-events-toggle-label${!isOn ? ' is-active' : ' is-inactive'}`}
                            aria-hidden="true"
                        >
                            {allLabel}
                        </span>
                        {switchButton}
                        <span
                            id={`${switchId}-label`}
                            className={`my-events-toggle-label${isOn ? ' is-active' : ' is-inactive'}`}
                            aria-hidden="true"
                        >
                            {label}
                        </span>
                        {infoButton}
                    </>
                ) : (
                    <>
                        <span className="my-events-toggle-label" id={`${switchId}-label`}>
                            {label}
                        </span>
                        {switchButton}
                        {infoButton}
                    </>
                )}
            </div>
            {((isOn ? onShowingText : offShowingText) || rightContent) && (
                <div className="my-events-showing-row">
                    {(isOn ? onShowingText : offShowingText) && (
                        <div className="my-events-showing-text">{isOn ? onShowingText : offShowingText}</div>
                    )}
                    {rightContent}
                </div>
            )}
        </div>
    );
}
